import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, FileArchive, FolderUp } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { createPointCloud, createPointCloudFromFolder, type FolderFile } from "../../lib/pointCloudsApi";

type UploadMode = "zip" | "folder";

interface UploadPointCloudModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  onCreated: () => void;
}

/**
 * Upload UI for either:
 *   - A Potree v2 .zip  → server extracts then serves the unpacked tree, OR
 *   - A raw folder containing the already-extracted Potree v2 files
 *     (metadata.json + hierarchy.bin + octree.bin and any extras).
 *
 * Folder uploads use the browser's `webkitdirectory` attribute, which is
 * supported in every Chromium/WebKit/Firefox build we care about; users on
 * an unsupported browser see the picker fall back to multi-file mode and
 * just shift+click the files.
 */
export function UploadPointCloudModal({
  isOpen, onClose, virtualSiteId, onCreated,
}: UploadPointCloudModalProps) {
  const zipInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [mode, setMode] = useState<UploadMode>("zip");
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [folderFiles, setFolderFiles] = useState<FolderFile[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalFolderBytes = useMemo(
    () => folderFiles.reduce((acc, f) => acc + f.file.size, 0),
    [folderFiles],
  );

  function reset() {
    setZipFile(null);
    setFolderFiles([]);
    setName("");
    setDescription("");
    setProgress(0);
    setError(null);
  }

  function handleClose() {
    if (isUploading) {
      abortRef.current?.abort();
      return;
    }
    reset();
    onClose();
  }

  function pickZip(picked: File) {
    setError(null);
    if (!picked.name.toLowerCase().endsWith(".zip")) {
      setError("Unsupported file. Upload a Potree v2 .zip (containing metadata.json).");
      return;
    }
    setZipFile(picked);
    if (!name) setName(picked.name.replace(/\.zip$/i, ""));
  }

  function pickFolder(fileList: FileList | null) {
    setError(null);
    if (!fileList || fileList.length === 0) return;
    const files: FolderFile[] = [];
    let rootDir: string | null = null;
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      // webkitRelativePath is non-empty when the user picked a folder.
      // For a folder pick of "MyScan/" containing metadata.json, the path
      // is "MyScan/metadata.json"; for multi-file picks, it's empty and we
      // fall back to the file name.
      const raw = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      const parts = raw.replace(/\\/g, "/").split("/").filter(Boolean);
      // Drop the top-level wrapper folder so the user-visible "MyScan" name
      // doesn't end up as part of the S3 key. The backend would silently
      // accept it but the canonical Potree layout has metadata.json at the
      // root of /potree/.
      if (parts.length > 1) {
        if (rootDir === null) rootDir = parts[0];
        if (parts[0] === rootDir) parts.shift();
      }
      const path = parts.join("/");
      if (!path) continue;
      files.push({ path, file });
    }
    // Sanity-check: must include metadata.json at the root.
    const hasMetadata = files.some((f) => f.path === "metadata.json");
    if (!hasMetadata) {
      setError("Selection must include a metadata.json at the folder root.");
      return;
    }
    setFolderFiles(files);
    if (!name && rootDir) setName(rootDir);
  }

  async function handleUpload() {
    setError(null);
    if (!name) {
      setError("Give the point cloud a name.");
      return;
    }
    if (mode === "zip" && !zipFile) {
      setError("Pick a .zip to upload.");
      return;
    }
    if (mode === "folder" && folderFiles.length === 0) {
      setError("Pick a folder containing the Potree v2 files.");
      return;
    }
    setIsUploading(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (mode === "zip" && zipFile) {
        await createPointCloud(
          {
            virtual_site_id: virtualSiteId,
            name,
            description: description || undefined,
            file: zipFile,
          },
          (loaded, total) => setProgress(Math.round((loaded / total) * 100)),
          { signal: controller.signal },
        );
      } else {
        await createPointCloudFromFolder(
          {
            virtual_site_id: virtualSiteId,
            name,
            description: description || undefined,
            files: folderFiles,
          },
          (loaded, total) => setProgress(Math.round((loaded / total) * 100)),
          { signal: controller.signal },
        );
      }
      onCreated();
      reset();
      onClose();
    } catch (err: unknown) {
      if (axios.isCancel(err) || (err as { name?: string })?.name === "CanceledError") {
        onCreated();
        reset();
        onClose();
        return;
      }
      const e = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 413) {
        setError(detail || "File exceeds the server upload limit.");
      } else if (e.response?.status === 403) {
        setError("You don't have permission to upload point clouds on this site.");
      } else {
        setError(detail || "Upload failed. Please try again.");
      }
    } finally {
      setIsUploading(false);
      abortRef.current = null;
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Point Cloud" size="lg">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Mode toggle — keep both flows on the same modal so users can
            switch without losing the name/description they typed. */}
        <div className="liquid-glass flex items-center gap-1 rounded-xl p-1">
          <ModeButton
            active={mode === "zip"}
            onClick={() => setMode("zip")}
            icon={FileArchive}
            label="Upload .zip"
            disabled={isUploading}
          />
          <ModeButton
            active={mode === "folder"}
            onClick={() => setMode("folder")}
            icon={FolderUp}
            label="Upload folder"
            disabled={isUploading}
          />
        </div>

        {mode === "zip" ? (
          <div
            onClick={() => zipInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) pickZip(dropped);
            }}
            className="group cursor-pointer rounded-xl border-2 border-dashed border-surface-700 bg-surface-900/40 px-6 py-10 text-center transition-colors hover:border-brand-500/60 hover:bg-brand-500/5"
          >
            <input
              ref={zipInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickZip(f);
              }}
            />
            {zipFile ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-500/15">
                  <FileArchive className="h-6 w-6 text-brand-300" />
                </div>
                <p className="text-sm font-medium text-surface-100">{zipFile.name}</p>
                <p className="text-xs text-surface-500">
                  {(zipFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-surface-800 group-hover:bg-brand-500/15">
                  <UploadCloud className="h-6 w-6 text-surface-400 group-hover:text-brand-300" />
                </div>
                <p className="text-sm font-medium text-surface-200">
                  Drop a Potree v2 .zip here, or click to browse
                </p>
                <p className="text-xs text-surface-500">
                  Archive must contain a metadata.json at the root or one folder deep
                </p>
              </div>
            )}
          </div>
        ) : (
          <div
            onClick={() => folderInputRef.current?.click()}
            className="group cursor-pointer rounded-xl border-2 border-dashed border-surface-700 bg-surface-900/40 px-6 py-10 text-center transition-colors hover:border-brand-500/60 hover:bg-brand-500/5"
          >
            <input
              ref={folderInputRef}
              type="file"
              // webkitdirectory makes the picker open in folder mode; the
              // React typing doesn't include it natively so we cast through
              // a Record. multiple is required so the FileList carries every
              // file under the chosen directory.
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
              multiple
              className="hidden"
              onChange={(e) => pickFolder(e.target.files)}
            />
            {folderFiles.length > 0 ? (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-500/15">
                  <FolderUp className="h-6 w-6 text-brand-300" />
                </div>
                <p className="text-sm font-medium text-surface-100">
                  {folderFiles.length} file{folderFiles.length === 1 ? "" : "s"} selected
                </p>
                <p className="text-xs text-surface-500">
                  {(totalFolderBytes / (1024 * 1024)).toFixed(1)} MB total
                </p>
                <ul className="mt-2 max-h-32 w-full overflow-y-auto rounded-md bg-surface-900/60 px-3 py-2 text-left text-[11px] font-mono text-surface-400">
                  {folderFiles.map((f) => (
                    <li key={f.path} className="truncate">
                      {f.path} · {(f.file.size / 1024).toFixed(1)} KB
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-surface-800 group-hover:bg-brand-500/15">
                  <FolderUp className="h-6 w-6 text-surface-400 group-hover:text-brand-300" />
                </div>
                <p className="text-sm font-medium text-surface-200">
                  Click to pick a folder
                </p>
                <p className="text-xs text-surface-500">
                  Folder must contain metadata.json + hierarchy.bin + octree.bin (Potree v2)
                </p>
              </div>
            )}
          </div>
        )}

        <Input
          label="Name"
          placeholder="e.g., Building A scan"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={isUploading}
        />
        <Input
          label="Description"
          placeholder="Optional"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isUploading}
        />

        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-surface-400">
              <span>Uploading…</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-800">
              <div
                className="h-full bg-gradient-to-r from-brand-500 to-purple-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-[11px] text-surface-500">
              {mode === "zip"
                ? "After upload, the server extracts the .zip in the background."
                : "Each file uploads directly to S3."}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose}>
            {isUploading ? "Cancel upload" : "Cancel"}
          </Button>
          <Button
            onClick={handleUpload}
            isLoading={isUploading}
            disabled={
              !name ||
              (mode === "zip" ? !zipFile : folderFiles.length === 0)
            }
          >
            Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ModeButton({
  active, onClick, icon: Icon, label, disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof FileArchive;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 " +
        (active
          ? "bg-brand-500/20 text-brand-200"
          : "text-surface-400 hover:bg-surface-800 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
